import i18next from 'i18next'
import {interfaces} from 'inversify'
import {register} from 'dna-react-ioc'

import {
    AuthStoreSymbol,
    ECOMTransactionSummaryStore,
    OverviewBetaTabsContainerStore,
    OverviewTabsContainerStore,
    PaginationStore,
    PaymentsStatuses,
    PaymentsTransactionsFilterStore,
    PaymentsTypes,
    PeriodType,
    POSTransactionsCardWithBarChartContainerStore,
    POSTransactionsComparisonCardWithBarChartContainerStore,
    POSTransactionSummaryStore,
    RangePickerStore,
    SelectContainerStore
} from '@/stores'
import {
    PaginationContainerStoreType,
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTabsContainerStore,
    TTransactionsCardWithBarChartContainerStore,
    TTransactionSummaryContainerStore
} from '@/components'
import {
    ITransactionsHistoryDataStore,
    OverviewChargedTransactionsCardWithBarChartContainerStoreSymbol,
    OverviewChargedTransactionsComparisonCardWithBarChartContainerStoreSymbol,
    OverviewTransactionSummaryStoreSymbol,
    PosAllTransactionsComparisonCardWithBarChartContainerStoreSymbol,
    PosChargedTransactionsCardWithBarChartContainerStoreSymbol,
    PosTransactionSummaryStoreSymbol,
    SettlementsTransactionsHistoryDataStoreSymbol,
    SettlementsTransactionsHistoryPaginationStoreSymbol,
} from '@/pages/Overview'
import {ECOMTransactionsCardWithBarChartContainerStore} from '@/stores/reports/TransactionsCardWithBarChartContainerStore/ECOMTransactionsCardWithBarChartContainerStore'
import {
    getPortalCurrencies,
    OnlinePaymentStatus,
    POSPaymentStateStatus
} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {ECOMTransactionsComparisonCardWithBarChartContainerStore} from '@/stores/reports/TransactionsComparisonCardWithBarChartContainerStore/ECOMTransactionsComparisonCardWithBarChartContainerStore'
import {EcomStoreSelectStore, PosStoreSelectStore} from '@/stores/store-and-terminals'
import {
    convertEcomStatusesToSelectItems,
    convertPaymentsStatusesToSelectItems,
    convertPaymentsTypesToSelectItems,
    convertPosStatesToSelectItems
} from '@/utils/transaction'
import {SettlementsTransactionSummaryStore} from '@/stores/reports/SettlementsTransactionSummaryStore/SettlementsTransactionSummaryStore'
import {
    OverviewSettlementsTransactionsCardWithBarChartContainerStoreSymbol,
    OverviewSettlementsTransactionSummaryStoreSymbol
} from '@/pages/Overview/components/OverviewSettlementsAnalytics'
import {SettlementsTransactionsCardWithBarChartContainerStore} from '@/stores/reports/SettlementsTransactionsCardWithBarChartContianerStore/SettlementsTransactionsCardWithBarChartContainerStore'
import {PaymentsTransactionsHistoryDataStore} from '@/stores/reports/PaymentsTransactionsHistoryDataStore'
import {SettlementsTransactionsHistoryDataStore} from '@/stores/reports/SettlementsTransactionsHistoryDataStore'
import {
    OverviewPaymentsTransactionsCardWithBarChartContainerStoreSymbol,
    OverviewPaymentsTransactionSummaryStoreSymbol,
    PaymentsTransactionsStatusStoreSymbol
} from '@/pages/Overview/components/OverviewPaymentsAnalytics'
import {PaymentsTransactionsCardWithBarChartContainerStore} from '@/stores/reports/PaymentsTransactionsCardWithBarChartContianerStore/PaymentsTransactionsCardWithBarChartContainerStore'
import {PaymentsTransactionSummaryStore} from '@/stores/reports/PaymentsTransactionSummaryStore'
import {
    PaymentsTransactionsHistoryDataStoreSymbol,
    PaymentsTransactionsHistoryPaginationStoreSymbol
} from '@/pages/Overview/components/PaymentsTransactionsHistoryTable/PaymentsTransactionsHistoryTable'
import {
    IPaymentsTransactionsFilterStore,
    PaymentsTransactionsFilterStoreSymbol
} from '~/code/pages/Overview/components/OveriviewPaymentsFilter'
import { EcomOverviewRangePickerStoreSymbol, OverviewECOMCurrencySelectStoreSymbol, OverviewEcomStoreSelectStoreSymbol, OverviewEcomTransactionsStatusStoreSymbol, OverviewPOSCurrencySelectStoreSymbol, OverviewPosStoreSelectStoreSymbol, OverviewTabsContainerStoreSymbol, PosOverviewRangePickerStoreSymbol } from '~/code/pages/Overview/constants'
import { OverviewBetaTabsContainerStoreSymbol, OverviewPaymentsCurrencySelectStoreSymbol, OverviewSettlementsCurrencySelectStoreSymbol, PaymentsOverviewRangePickerStoreSymbol, PaymentsTypeSelectStoreSymbol, SettlementsOverviewRangePickerStoreSymbol } from '~/code/pages/Overview/constants-beta'

export const overviewState = () => {
    /** filter stores */

    register<TRangePickerContainerStore>(EcomOverviewRangePickerStoreSymbol)
        .toDynamicValue(() => new RangePickerStore({period: PeriodType.Day}))
        .inSingletonScope()

    register<TRangePickerContainerStore>(PosOverviewRangePickerStoreSymbol)
        .toDynamicValue(() => new RangePickerStore({period: PeriodType.Day}))
        .inSingletonScope()

    register<TRangePickerContainerStore>(SettlementsOverviewRangePickerStoreSymbol)
        .toDynamicValue(() =>new RangePickerStore({
            period: PeriodType.Last7Days, 
            rangePresets: {
                        [i18next.t('RangePicker.presets.today')]: PeriodType.Day,
                        [i18next.t('RangePicker.presets.yesterday')]: PeriodType.Yesterday,
                        [i18next.t('RangePicker.presets.thisWeek')]: PeriodType.Week,
                        [i18next.t('RangePicker.presets.last7Days')]: PeriodType.Last7Days,
                        [i18next.t('RangePicker.presets.thisMonth')]: PeriodType.Month,
                        [i18next.t('RangePicker.presets.last30Days')]: PeriodType.Last30Days
                    }}))
        .inSingletonScope()

    register<TRangePickerContainerStore>(PaymentsOverviewRangePickerStoreSymbol)
        .toDynamicValue(() => new RangePickerStore({
            period: PeriodType.Last7Days,
            rangePresets: {
                        [i18next.t('RangePicker.presets.today')]: PeriodType.Day,
                        [i18next.t('RangePicker.presets.yesterday')]: PeriodType.Yesterday,
                        [i18next.t('RangePicker.presets.thisWeek')]: PeriodType.Week,
                        [i18next.t('RangePicker.presets.last7Days')]: PeriodType.Last7Days,
                        [i18next.t('RangePicker.presets.thisMonth')]: PeriodType.Month,
                        [i18next.t('RangePicker.presets.last30Days')]: PeriodType.Last30Days
                    }
        }))
        .inSingletonScope()

    register<TSelectContainerStore>(OverviewECOMCurrencySelectStoreSymbol)
        .toDynamicValue(() => new SelectContainerStore({options: getPortalCurrencies}))
        .inSingletonScope()

    register<TSelectContainerStore>(OverviewPOSCurrencySelectStoreSymbol)
        .toDynamicValue(() => new SelectContainerStore({options: getPortalCurrencies}))
        .inSingletonScope()

    register<TSelectContainerStore>(OverviewSettlementsCurrencySelectStoreSymbol)
        .toDynamicValue(
            () =>
                new SelectContainerStore({
                    options: [DEFAULT_CURRENCY].map((item) => ({
                        label: item,
                        value: item
                    }))
                })
        )
        .inSingletonScope()

    register<TSelectContainerStore>(OverviewPaymentsCurrencySelectStoreSymbol)
        .toDynamicValue(() => new SelectContainerStore({options: getPortalCurrencies}))
        .inSingletonScope()

    register<EcomStoreSelectStore>(OverviewEcomStoreSelectStoreSymbol)
        .to(EcomStoreSelectStore)
        .inSingletonScope()

    register<PosStoreSelectStore>(OverviewPosStoreSelectStoreSymbol)
        .to(PosStoreSelectStore)
        .inSingletonScope()

    register<TTabsContainerStore>(OverviewTabsContainerStoreSymbol)
        .to(OverviewTabsContainerStore)
        .inSingletonScope()

    register<TTabsContainerStore>(OverviewBetaTabsContainerStoreSymbol)
        .to(OverviewBetaTabsContainerStore)
        .inSingletonScope()

    register<TSelectContainerStore>(OverviewEcomTransactionsStatusStoreSymbol)
        .toDynamicValue(
            () =>
                new SelectContainerStore({
                    value: OnlinePaymentStatus.charge,
                    options: convertEcomStatusesToSelectItems([OnlinePaymentStatus.charge])
                })
        )
        .inSingletonScope()

    register<PaginationContainerStoreType>(SettlementsTransactionsHistoryPaginationStoreSymbol)
        .to(PaginationStore)
        .inSingletonScope()

    register<PaginationContainerStoreType>(PaymentsTransactionsHistoryPaginationStoreSymbol)
        .to(PaginationStore)
        .inSingletonScope()

    register<TSelectContainerStore>(PaymentsTransactionsStatusStoreSymbol)
        .toDynamicValue(
            () =>
                new SelectContainerStore({
                    value: PaymentsStatuses[0],
                    options: convertPaymentsStatusesToSelectItems(PaymentsStatuses)
                })
        )
        .inSingletonScope()

    register<TSelectContainerStore>(PaymentsTypeSelectStoreSymbol)
        .toDynamicValue(
            () =>
                new SelectContainerStore({
                    value: PaymentsTypes[0],
                    options: convertPaymentsTypesToSelectItems(PaymentsTypes)
                })
        )
        .inSingletonScope()

    register<IPaymentsTransactionsFilterStore>(PaymentsTransactionsFilterStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new PaymentsTransactionsFilterStore(
                    context.container.get(OverviewPosStoreSelectStoreSymbol),
                    context.container.get(OverviewEcomStoreSelectStoreSymbol),
                    context.container.get(PaymentsTypeSelectStoreSymbol),
                    context.container.get(PaymentsTransactionsStatusStoreSymbol),
                    context.container.get(OverviewPaymentsCurrencySelectStoreSymbol),
                    context.container.get(PaymentsOverviewRangePickerStoreSymbol)
                )
        )
        .inSingletonScope()

    /** data stores */

    register<TTransactionSummaryContainerStore>(OverviewTransactionSummaryStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new ECOMTransactionSummaryStore(
                    'overview.ECOMTransactionSummaryStore',
                    context.container.get(EcomOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewECOMCurrencySelectStoreSymbol),
                    context.container.get(OverviewEcomStoreSelectStoreSymbol)
                )
        )
        .inSingletonScope()

    register<TTransactionSummaryContainerStore>(PosTransactionSummaryStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new POSTransactionSummaryStore(
                    'overview.POSTransactionSummaryStore',
                    context.container.get(PosOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewPOSCurrencySelectStoreSymbol),
                    context.container.get(OverviewPosStoreSelectStoreSymbol)
                )
        )
        .inSingletonScope()

    register<TTransactionSummaryContainerStore>(OverviewSettlementsTransactionSummaryStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new SettlementsTransactionSummaryStore(
                    'overview.SettlementsTransactionSummaryStore',
                    context.container.get(SettlementsOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewSettlementsCurrencySelectStoreSymbol)
                )
        )
        .inSingletonScope()

    register<TTransactionSummaryContainerStore>(OverviewPaymentsTransactionSummaryStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new PaymentsTransactionSummaryStore(
                    'overview.PaymentsTransactionSummaryStore',
                    context.container.get(PaymentsOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewPaymentsCurrencySelectStoreSymbol),
                    context.container.get(PaymentsTransactionsFilterStoreSymbol)
                )
        )
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        OverviewChargedTransactionsComparisonCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const ecomOverviewTransactionsComparisonCardWithBarChartStore =
                new ECOMTransactionsComparisonCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(EcomOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewECOMCurrencySelectStoreSymbol),
                    context.container.get(OverviewEcomStoreSelectStoreSymbol),
                    context.container.get(OverviewEcomTransactionsStatusStoreSymbol)
                )

            return ecomOverviewTransactionsComparisonCardWithBarChartStore
        })
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        PosAllTransactionsComparisonCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const posOverviewTransactionsComparisonCardWithBarChartStore =
                new POSTransactionsComparisonCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(PosOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewPOSCurrencySelectStoreSymbol),
                    context.container.get(OverviewPosStoreSelectStoreSymbol)
                )
            posOverviewTransactionsComparisonCardWithBarChartStore.setSelectedStatus(
                POSPaymentStateStatus.all
            )
            posOverviewTransactionsComparisonCardWithBarChartStore.statuses =
                convertPosStatesToSelectItems([POSPaymentStateStatus.all])

            return posOverviewTransactionsComparisonCardWithBarChartStore
        })
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        OverviewChargedTransactionsCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const ecomOverviewTransactionsCardWithBarChartStore =
                new ECOMTransactionsCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(EcomOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewECOMCurrencySelectStoreSymbol),
                    false,
                    context.container.get(
                        OverviewChargedTransactionsComparisonCardWithBarChartContainerStoreSymbol
                    ),
                    context.container.get(OverviewEcomStoreSelectStoreSymbol),
                    context.container.get(OverviewEcomTransactionsStatusStoreSymbol)
                )

            return ecomOverviewTransactionsCardWithBarChartStore
        })
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        PosChargedTransactionsCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const posOverviewTransactionsCardWithBarChartStore =
                new POSTransactionsCardWithBarChartContainerStore(
                    'overview.POSTransactionsCardWithBarChartContainerStore',
                    context.container.get(AuthStoreSymbol),
                    context.container.get(PosOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewPOSCurrencySelectStoreSymbol),
                    false,
                    context.container.get(
                        PosAllTransactionsComparisonCardWithBarChartContainerStoreSymbol
                    ),
                    context.container.get(OverviewPosStoreSelectStoreSymbol)
                )

            posOverviewTransactionsCardWithBarChartStore.setSelectedStatus(
                POSPaymentStateStatus.all
            )
            posOverviewTransactionsCardWithBarChartStore.statuses = convertPosStatesToSelectItems([
                POSPaymentStateStatus.all
            ])
            return posOverviewTransactionsCardWithBarChartStore
        })
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        OverviewSettlementsTransactionsCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const settlementsOverviewTransactionsComparisonCardWithBarChartStore =
                new SettlementsTransactionsCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(SettlementsOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewSettlementsCurrencySelectStoreSymbol)
                )

            return settlementsOverviewTransactionsComparisonCardWithBarChartStore
        })
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        OverviewPaymentsTransactionsCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const paymentsOverviewTransactionsComparisonCardWithBarChartStore =
                new PaymentsTransactionsCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(PaymentsTransactionsFilterStoreSymbol)
                )

            return paymentsOverviewTransactionsComparisonCardWithBarChartStore
        })
        .inSingletonScope()

    register<ITransactionsHistoryDataStore>(SettlementsTransactionsHistoryDataStoreSymbol)
        .toDynamicValue((context: interfaces.Context) => {
            const settlementsTransactionsHistoryDataStore =
                new SettlementsTransactionsHistoryDataStore(
                    context.container.get(SettlementsOverviewRangePickerStoreSymbol),
                    context.container.get(OverviewSettlementsCurrencySelectStoreSymbol),
                    context.container.get(SettlementsTransactionsHistoryPaginationStoreSymbol)
                )
            return settlementsTransactionsHistoryDataStore
        })
        .inSingletonScope()

    register<ITransactionsHistoryDataStore>(PaymentsTransactionsHistoryDataStoreSymbol)
        .toDynamicValue((context: interfaces.Context) => {
            const paymentsTransactionsHistoryDataStore = new PaymentsTransactionsHistoryDataStore(
                context.container.get(PaymentsOverviewRangePickerStoreSymbol),
                context.container.get(OverviewPaymentsCurrencySelectStoreSymbol),
                context.container.get(PaymentsTransactionsHistoryPaginationStoreSymbol)
            )
            return paymentsTransactionsHistoryDataStore
        })
        .inSingletonScope()
}
